import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import { Flex, Banner, defaultThemeProp } from '@pega/cosmos-react-core';
import type {
  BannerProps,
  ForwardRefForwardPropsComponent,
  NoChildrenProp
} from '@pega/cosmos-react-core';
import { StyledBanner } from '@pega/cosmos-react-core/lib/components/Banner/Banner.styles';

import ChatBody from './ChatBody';
import ChatComposer from './ChatComposer';
import type { ChatComposerProps } from './ChatComposer';
import ChatHeader from './ChatHeader';
import type { ChatHeaderProps } from './ChatHeader';
import SuggestedReplyPicker from './SuggestedReplyPicker';
import type { SuggestedReplyPickerProps } from './SuggestedReplyPicker';
import type { ChatBodyProps } from './Chat.types';

export type ChatBannerProps = {
  /** Content */
  content: string;
  /** Name of the icon */
  icon: string;
};

export interface ChatProps extends NoChildrenProp {
  /** ref to the element */
  ref?: Ref<HTMLElement>;
  /** Header of conversation pane */
  header: ChatHeaderProps;
  /** Wrapper of message groups, system messages, type ahead */
  body: ChatBodyProps;
  /** Wrapper of suggested replies */
  suggestedReplyPicker?: SuggestedReplyPickerProps;
  /** chat banner */
  banner?: Pick<BannerProps, 'variant' | 'messages'>;
  /** Wrapper of conversation input, emojis, attachments */
  composer: ChatComposerProps;
}

const StyledUtilityContainer = styled.div``;
const StyledUtilityMerger = styled.div``;
const StyledUtilityContent = styled.div``;

const StyledChatContainer = styled.section(props => {
  const {
    theme: {
      base: {
        spacing,
        shadow: { high: shadowHigh },
        palette: {
          'primary-background': primaryBackground,
          'secondary-background': secondaryBackground
        },
        'border-radius': borderRadius
      }
    }
  } = props;
  return css`
    height: 100%;
    background: ${primaryBackground};
    width: 100%;
    border-radius: ${borderRadius};
    ${StyledUtilityMerger} {
      min-height: 15rem;
      position: relative;
      height: 100%;
      overflow-y: hidden;
    }
    ${StyledUtilityContainer} {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 2;
      max-height: 60%;
      width: 100%;
      background-color: ${secondaryBackground};
      > ${StyledUtilityContent} {
        padding: ${spacing} 0;
        box-shadow: ${shadowHigh};
        height: auto;
        width: 100%;
        overflow-y: auto;
        background:
          linear-gradient(${secondaryBackground} 10%, rgba(255, 255, 255, 0)),
          linear-gradient(rgba(255, 255, 255, 0), ${secondaryBackground} 10%) 0 100%,
          radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)),
          radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) 0 100%;
        background-repeat: no-repeat;
        background-size:
          100% 1rem,
          100% 1rem,
          100% 0.25rem,
          100% 0.25rem;
        background-attachment: local, local, scroll, scroll;
      }
    }
    ${StyledBanner} {
      padding: calc(2 * ${spacing});
      margin-bottom: 0;
      flex-shrink: 0;
    }
  `;
});

StyledChatContainer.defaultProps = defaultThemeProp;

const Chat: ForwardRefForwardPropsComponent<ChatProps> = forwardRef(function Chat(
  {
    header,
    body,
    composer,
    banner,
    suggestedReplyPicker,
    ...restProps
  }: PropsWithoutRef<ChatProps>,
  ref: ChatProps['ref']
) {
  return (
    <Flex
      ref={ref}
      as={StyledChatContainer}
      container={{
        direction: 'column'
      }}
      {...restProps}
    >
      <ChatHeader {...header} />
      {banner && <Banner variant={banner.variant} messages={banner.messages} />}
      <ChatBody {...body} />
      {suggestedReplyPicker && <SuggestedReplyPicker {...suggestedReplyPicker} />}
      <ChatComposer {...composer} />
    </Flex>
  );
});

export default Chat;
